import React, { useState } from 'react';
import {
    IonCard,
    IonCardHeader,
    IonToolbar,
    IonButtons,
    IonButton,
    IonIcon,
    IonTitle,
    IonGrid,
    IonRow,
    IonCol,
    IonInput,
    IonSpinner,
    IonInputPasswordToggle,
    IonText,
    IonList,
    IonItem,
    IonLabel,
    IonChip,
} from '@ionic/react';
import { arrowBack, checkmarkCircle, lockClosed, checkmark, close } from 'ionicons/icons';
import styles from '../../pages/login/LoginReg.module.scss';
import {
    PasswordRequirement,
    getPasswordRequirements,
    validatePasswordRequirements,
    checkPasswordsMatch,
    areAllRequirementsMet
} from '../../utils/passwordValidation';
import { useTranslation } from 'react-i18next';

interface RegisterPasswordCardProps {
    formData: {
        password: string;
        confirmPassword: string;
    };
    uiState: {
        registering: boolean;
    };
    authLoading: boolean;
    isDisabled: boolean;
    onGoBack: () => void;
    onRegister: () => void;
    onInputChange: (field: string, value: string) => void;
}

export const RegisterPasswordCard: React.FC<RegisterPasswordCardProps> = ({
    formData,
    uiState,
    authLoading,
    isDisabled,
    onGoBack,
    onRegister,
    onInputChange
}) => {
  const { t } = useTranslation();
    const [passwordRequirements, setPasswordRequirements] = useState<PasswordRequirement[]>(getPasswordRequirements());

    const [passwordsMatch, setPasswordsMatch] = useState(false);

    // Re-validates whenever password/confirmPassword change — computed
    // during render (React's "adjust state when a dependency changes"
    // pattern) rather than as setState calls inside an effect, since it's a
    // pure derivation with no external system involved.
    const [prevPasswordInputs, setPrevPasswordInputs] = useState({ password: formData.password, confirmPassword: formData.confirmPassword });
    if (prevPasswordInputs.password !== formData.password || prevPasswordInputs.confirmPassword !== formData.confirmPassword) {
        setPrevPasswordInputs({ password: formData.password, confirmPassword: formData.confirmPassword });
        setPasswordRequirements(validatePasswordRequirements(formData.password));
        setPasswordsMatch(checkPasswordsMatch(formData.password, formData.confirmPassword));
    }

    const allRequirementsMet = areAllRequirementsMet(passwordRequirements, passwordsMatch);

    return (
        <IonCard className={`${styles.registerCard} ion-text-center`}>
            <IonCardHeader className={styles.cardHeader}>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton
                            fill="clear"
                            className={styles.backButton}
                            onClick={onGoBack}
                            disabled={isDisabled}
                        >
                            <IonIcon icon={arrowBack} />
                        </IonButton>
                    </IonButtons>
                    <IonTitle className={styles.cardTitle}>{t('components.AuthComponents.RegisterPasswordCard.createPassword')}</IonTitle>
                </IonToolbar>
            </IonCardHeader>
            <IonGrid>
                <IonRow>
                    <IonCol>
                        <form onSubmit={(e) => { e.preventDefault(); onRegister(); }}>
                            <IonInput
                                className={`${styles.customInput} ion-margin-top`}
                                labelPlacement="stacked"
                                label={t('components.AuthComponents.RegisterPasswordCard.password')}
                                type="password"
                                placeholder={t('components.AuthComponents.RegisterPasswordCard.password')}
                                value={formData.password}
                                onIonInput={(e) => onInputChange('password', e.detail.value!)}
                                disabled={isDisabled}
                                required
                            >
                                <IonIcon icon={lockClosed} slot="start" />
                                <IonInputPasswordToggle slot="end" id="password-toggle"></IonInputPasswordToggle>
                            </IonInput>
                            <IonInput
                                name="register-confirm-password-input"
                                className={`${styles.customInput} ion-margin-top`}
                                labelPlacement="stacked"
                                label={t('components.AuthComponents.RegisterPasswordCard.confirmPassword')}
                                type="password"
                                placeholder={t('components.AuthComponents.RegisterPasswordCard.confirmPassword')}
                                value={formData.confirmPassword}
                                onIonInput={(e) => onInputChange('confirmPassword', e.detail.value!)}
                                disabled={isDisabled}
                                required
                            >
                                <IonIcon icon={checkmarkCircle} slot="start" style={{ color: passwordsMatch ? '#10b981' : '#ffffff' }} />
                                <IonInputPasswordToggle slot="end" id="confirm-password-toggle"></IonInputPasswordToggle>
                            </IonInput>
                            <IonCard className={`${styles.requirementsContainer} ion-margin-top`}>
                                <IonList>
                                    <IonItem lines="none" className={styles.requirementsSummary}>
                                        <IonLabel>
                                            <IonText>
                                                {allRequirementsMet ? t('components.AuthComponents.RegisterPasswordCard.allRequirementsMet') : `${passwordRequirements.filter(r => r.met).length + (passwordsMatch ? 1 : 0)}/${passwordRequirements.length + 1} requirements met`}
                                            </IonText>
                                        </IonLabel>
                                    </IonItem>
                                    <IonItem lines="none" className={styles.requirementsList}>
                                        <IonRow className={styles.chipsContainer}>
                                            {passwordRequirements.map((req) => (
                                                <IonCol size="auto" key={req.id}>
                                                    <IonChip
                                                        className={`${styles.requirementChip} ${req.met ? styles.met : styles.unmet}`}
                                                    >
                                                        <IonIcon icon={req.met ? checkmark : close} className={styles.chipIcon} />
                                                        <IonLabel className={styles.chipText}>{req.label}</IonLabel>
                                                    </IonChip>
                                                </IonCol>
                                            ))}
                                            <IonCol size="auto">
                                                <IonChip
                                                    className={`${styles.requirementChip} ${passwordsMatch ? styles.met : styles.unmet}`}
                                                >
                                                    <IonIcon icon={passwordsMatch ? checkmark : close} className={styles.chipIcon} />
                                                    <IonLabel className={styles.chipText}>{t('components.AuthComponents.RegisterPasswordCard.passwordsMatch')}</IonLabel>
                                                </IonChip>
                                            </IonCol>
                                        </IonRow>
                                    </IonItem>
                                </IonList>
                            </IonCard>

                            <IonRow className={styles.centerCol}>
                                <IonCol size="auto">
                                    <IonButton
                                        expand="block"
                                        className={`ion-margin-top ion-margin-bottom ${styles.customButton} ${styles.mainButton}`}
                                        type="submit"
                                        disabled={uiState.registering || authLoading || !allRequirementsMet}
                                    >
                                        {uiState.registering ? (
                                            <>{t('components.AuthComponents.RegisterPasswordCard.registering')}
                                                <IonSpinner name="dots" slot="end" />
                                            </>
                                        ) : t('components.AuthComponents.RegisterPasswordCard.register')}
                                    </IonButton>
                                </IonCol>
                            </IonRow>
                        </form>
                    </IonCol>
                </IonRow>
            </IonGrid>
        </IonCard>
    );
};
